<template>
    <div
        v-if="iframeUrl"
        class="admin-next-iframe-container"
        :class="fullView ? 'admin-next-iframe-container--full' : 'admin-next-iframe-container--inline'">
        <iframe
            ref="iframe"
            :src="iframeUrl"
            class="admin-next-iframe"
            frameborder="0"
            :allow="allow"
            allowfullscreen>
        </iframe>
    </div>
    <div v-else>
        <slot name="fallback" />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

/**
 * Embeds a next-admin page and handles the parent <-> iframe contract:
 *  - builds the iframe URL with the session params (token, lang, adminOrigin,
 *    and currency when `withCurrency` is set) followed by `query`
 *  - forwards locale/currency changes to the iframe (`langSelect`, `currencySelect`)
 *  - re-emits the iframe messages:
 *      `back`     on `navigate:back` (falls back to `$router.go(-1)` without listener)
 *      `navigate` on `navigate`, with the iframe path
 *      `message`  for every message, with the raw event
 *
 * The URL is built once, on creation: later route or prop changes do not reload
 * the iframe (the iframe drives its own navigation). Use `:key` on this
 * component to force a reload.
 */
export default {
    name: 'AdminNextIframe',
    props: {
        // next-admin route, e.g. "/catalog-manager/"
        path: {
            type: String,
            required: true
        },
        // Extra query params (empty values are skipped)
        query: {
            type: Object,
            default: () => ({})
        },
        withCurrency: {
            type: Boolean,
            default: false
        },
        // Cover the whole content area (drawers/dialogs inside the iframe then
        // span the full view). Set to false to keep the iframe in the page flow.
        fullView: {
            type: Boolean,
            default: true
        },
        // iframe `allow` attribute, e.g. "clipboard-write"
        allow: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            iframeUrl: null
        }
    },
    computed: {
        ...mapGetters(['activeUser', 'currentLocale', 'currentCurrency']),
    },
    created() {
        this.iframeUrl = this.buildUrl();
    },
    mounted() {
        window.addEventListener('message', this.onMessage);
    },
    beforeDestroy() {
        window.removeEventListener('message', this.onMessage);
    },
    watch: {
        currentLocale(locale) {
            this.sendMessage('langSelect', { locale });
        },
        currentCurrency(currency) {
            this.sendMessage('currencySelect', { currency });
        }
    },
    methods: {
        buildUrl() {
            const base = process.env.VUE_APP_ADMIN_NEXT_URL;
            const token = this.activeUser?.token;

            if (!base || !token) {
                return null;
            }

            const params = new URLSearchParams({
                token,
                lang: this.currentLocale || 'fr',
                adminOrigin: window.location.origin
            });

            if (this.withCurrency && this.currentCurrency) {
                params.set('currency', this.currentCurrency);
            }

            Object.entries(this.query).forEach(([key, value]) => {
                if (value != null && value !== '') {
                    params.set(key, value);
                }
            });

            return `${base}${this.path}?${params.toString()}`;
        },
        onMessage(event) {
            const type = event.data?.type;

            if (type === 'navigate:back') {
                if (this.$listeners.back) {
                    this.$emit('back');
                } else {
                    this.$router.go(-1);
                }
            }

            if (type === 'navigate') {
                const path = event.data.data?.path;
                if (path && typeof path === 'string') {
                    this.$emit('navigate', path);
                }
            }

            this.$emit('message', event);
        },
        sendMessage(type, data = {}) {
            const iframe = this.$refs.iframe;
            if (iframe?.contentWindow) {
                iframe.contentWindow.postMessage({ type, data }, '*');
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.admin-next-iframe-container {
    overflow: hidden;

    &--full {
        position: absolute;
        inset: 0;
        margin: 0;
        padding: 0;

        @media screen and (max-width: 1024px) {
            height: calc(100svh - 50px);
        }
    }

    &--inline {
        width: 100%;
        height: calc(100vh - 64px);
    }
}

.admin-next-iframe {
    width: 100%;
    height: 100%;
    border: none;
    display: block;
}
</style>
